import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { KnowledgeArticle, fetchPublicKnowledgeArticle } from '@/services/knowledge';
import { withImageFallback } from '@/utils/images';

function resolveAbsoluteUrl(value?: string | null) {
  if (!value) return '';
  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return value;
  }
}

function setMetaTag(selector: string, attributes: Record<string, string>, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  const created = !tag;
  const previousContent = tag?.getAttribute('content');

  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => tag?.setAttribute(key, value));
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);

  return () => {
    if (!tag) return;
    if (created) {
      tag.remove();
      return;
    }
    if (previousContent === null || previousContent === undefined) {
      tag.removeAttribute('content');
      return;
    }
    tag.setAttribute('content', previousContent);
  };
}

export default function ArticleDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [article, setArticle] = useState<KnowledgeArticle | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<KnowledgeArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!id) {
      navigate('/knowledge', { replace: true });
      return;
    }
    setLoading(true);
    fetchPublicKnowledgeArticle(id)
      .then((data) => {
        if (!alive) return;
        if (!data.article) {
          navigate('/knowledge', { replace: true });
          return;
        }
        setArticle(data.article);
        setRelatedArticles(data.relatedArticles);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!article) return;

    const previousTitle = document.title;
    const seoTitle = article.metaTitle?.trim() || article.title;
    const seoDescription = article.metaDescription?.trim() || article.summary;
    const restoreMetaTags: Array<() => void> = [];

    if (seoTitle) {
      document.title = `${seoTitle}｜富聆聽健康`;
      restoreMetaTags.push(
        setMetaTag('meta[property="og:title"]', { property: 'og:title' }, seoTitle),
        setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }, seoTitle),
      );
    }

    if (seoDescription) {
      restoreMetaTags.push(
        setMetaTag('meta[name="description"]', { name: 'description' }, seoDescription),
        setMetaTag('meta[property="og:description"]', { property: 'og:description' }, seoDescription),
        setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }, seoDescription),
      );
    }

    const imageUrl = resolveAbsoluteUrl(article.image);
    if (imageUrl) {
      restoreMetaTags.push(
        setMetaTag('meta[property="og:image"]', { property: 'og:image' }, imageUrl),
        setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image' }, imageUrl),
      );
    }

    restoreMetaTags.push(
      setMetaTag('meta[property="og:type"]', { property: 'og:type' }, 'article'),
      setMetaTag('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image'),
    );

    return () => {
      document.title = previousTitle;
      restoreMetaTags.forEach((restore) => restore());
    };
  }, [article]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="bg-brand-green py-12 md:py-16">
            <div className="mx-auto max-w-4xl px-6 md:px-10 lg:px-16">
              <div className="h-5 w-32 animate-pulse rounded bg-white/20" />
              <div className="mt-5 h-8 w-3/4 animate-pulse rounded bg-white/20" />
              <div className="mt-4 h-5 w-1/2 animate-pulse rounded bg-white/15" />
            </div>
          </div>
          <div className="mx-auto max-w-4xl px-6 py-10 md:px-10 md:py-16 lg:px-16">
            <div className="mb-8 h-52 animate-pulse rounded-2xl bg-[#f1ece4] md:h-80" />
            <div className="space-y-4">
              <div className="h-5 animate-pulse rounded bg-[#f1ece4]" />
              <div className="h-5 w-5/6 animate-pulse rounded bg-[#f1ece4]" />
              <div className="h-5 w-2/3 animate-pulse rounded bg-[#f1ece4]" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        {/* 文章標頭 */}
        <div className="bg-brand-green text-white py-12 md:py-16 animate-fade-in-up">
          <div className="px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/knowledge')}
              className="text-white/80 text-lg flex items-center gap-1 hover:text-white transition-colors mb-3 py-2"
            >
              <i className="ri-arrow-left-line" /> 返回聽力知識
            </button>
            <span className="px-3 py-1.5 rounded-full bg-white/20 text-base font-medium">
              {article.category}
            </span>
            <h1 className="mt-4 text-2xl md:text-3xl font-bold leading-snug">
              {article.title}
            </h1>
            <div className="mt-4 flex items-center gap-4 text-white/80 text-lg flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" /> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" /> {article.readTime} 分鐘閱讀
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" /> {article.date}
              </span>
            </div>
          </div>
        </div>

        {/* 文章內容 */}
        <div className="px-6 md:px-10 lg:px-16 max-w-4xl mx-auto py-10 md:py-16 animate-fade-in-up delay-100">
          {/* 封面圖 */}
          <div className="rounded-2xl overflow-hidden h-52 md:h-80 mb-8">
            <img
              src={withImageFallback(article.image)}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* 文章內文 */}
          <div className="space-y-6">
            <p className="text-gray-700 leading-relaxed text-base md:text-lg">{article.summary}</p>
            <div className="prose prose-lg max-w-none text-gray-700 prose-headings:text-[#1B3D2A] prose-a:text-[#1B3D2A] prose-img:rounded-xl prose-iframe:aspect-video prose-iframe:w-full prose-iframe:rounded-xl" dangerouslySetInnerHTML={{ __html: article.content }} />

            {/* 更多補充段落 */}
            <div className="bg-white rounded-xl border border-gray-50/50 p-5 md:p-7">
              <h3 className="text-lg font-bold text-gray-900 mb-3">專家建議</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                定期進行聽力檢查是維持聽力健康的最佳方式。建議 40 歲以上民眾每年至少檢查一次，若已有聽力問題或家族病史，則應每 6 個月檢查一次。
                富聆聽提供免費的聽力篩檢服務，歡迎隨時預約。
              </p>
            </div>

            <p className="text-gray-700 leading-relaxed text-base md:text-lg">
              如果您有任何聽力相關的疑問，或想了解更多關於助聽器的資訊，歡迎聯繫我們的專業團隊。我們將竭誠為您提供個人化的聽力保健建議。
            </p>
          </div>

          {/* 底部導航 */}
          <div className="mt-10 pt-8 border-t border-gray-50/50 flex flex-col sm:flex-row items-center justify-between gap-4">
            <button
              onClick={() => navigate('/knowledge')}
              className="text-brand-green text-base font-medium flex items-center gap-2 hover:underline py-2"
            >
              <i className="ri-arrow-left-line" /> 返回聽力知識列表
            </button>
            <button
              onClick={() => navigate('/booking')}
              className="bg-brand-green text-white px-5 py-2.5 rounded-xl text-base font-medium hover:bg-brand-green-dark transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-calendar-check-line" />
              預約免費聽力檢測
            </button>
          </div>
        </div>

        {/* 相關文章推薦 */}
        <div className="px-6 md:px-10 lg:px-16 pb-12 md:pb-20 max-w-7xl mx-auto animate-fade-in-up delay-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6">您可能也感興趣</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {relatedArticles.map((a) => (
              <div
                key={a.id}
                className="bg-white rounded-xl border border-gray-50/50 overflow-hidden cursor-pointer hover:border-gray-100 transition-all"
                onClick={() => navigate(`/article/${a.slug || a.id}`)}
              >
                <div className="w-full h-36 md:h-40 overflow-hidden">
                  <img
                    src={withImageFallback(a.image)}
                    alt={a.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <span className="px-3 py-1.5 rounded-full bg-brand-green/10 text-base text-brand-green font-medium">
                    {a.category}
                  </span>
                  <h4 className="mt-3 text-lg font-bold text-gray-900 leading-snug">
                    {a.title}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
